// (B) Plain(커스텀) Select 백엔드 — antd Select 대체(단일선택). 포지셔닝/포털/클릭아웃은 Radix Popover 재사용,
//   트리거(selector)·옵션 리스트 DOM 은 antd Select v5 를 이중클래스로 미러링:
//   .om-select(root) > .om-select-selector > (.om-select-selection-item|-placeholder) + .om-select-arrow
//   드롭다운: .om-select-dropdown > .om-select-item-option(.-selected/-active) > .om-select-item-option-content
//   각 요소 antd 원본 .om-select-* 동시 부여 → 기존 소비처/전역 SCSS(ninegqlUI 공유표면 포함) 무변경 매칭.
//   시각 base 는 styles/omSelect.css(:where 0-특이도).
import * as PopoverPrimitive from '@radix-ui/react-popover'
import { useMemo, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react'
import type { OmwSelectOption, OmwSelectProps } from '../api/OmwSelect'
import { cx } from './cx'
import '../styles/omwSelect.css'

const VARIANT_CLASS = {
  outlined: 'om-select-outlined',
  borderless: 'om-select-borderless',
  filled: 'om-select-filled',
} as const

export function PlainSelect<T extends string | number>({
  value,
  defaultValue,
  onChange,
  options,
  placeholder,
  disabled,
  size = 'md',
  suffixIcon,
  popupClassName,
  variant = 'outlined',
  popupMatchSelectWidth = true,
  getPopupContainer,
  labelRender,
  optionRender,
  tabIndex,
  onClick,
  className,
  style,
  'data-testid': dataTestid,
}: OmwSelectProps<T>) {
  const [open, setOpen] = useState(false)
  const [innerValue, setInnerValue] = useState<T | undefined>(defaultValue)
  const [activeIndex, setActiveIndex] = useState(-1)
  const triggerRef = useRef<HTMLDivElement>(null)

  const currentValue = value !== undefined ? value : innerValue
  const selectedIndex = useMemo(
    () => options.findIndex((o) => o.value === currentValue),
    [options, currentValue],
  )
  const selectedOption = selectedIndex >= 0 ? options[selectedIndex] : undefined

  const commit = (opt: OmwSelectOption<T>) => {
    if (opt.disabled) return
    if (value === undefined) setInnerValue(opt.value)
    onChange?.(opt.value)
    setOpen(false)
    triggerRef.current?.focus()
  }

  const moveActive = (dir: 1 | -1) => {
    const n = options.length
    if (n === 0) return
    let i = activeIndex
    for (let step = 0; step < n; step++) {
      i = (i + dir + n) % n
      if (!options[i]?.disabled) break
    }
    setActiveIndex(i)
  }

  const onOpenChange = (next: boolean) => {
    if (disabled) return
    setOpen(next)
    if (next) setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0)
  }

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        onOpenChange(true)
      }
      return
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      moveActive(1)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      moveActive(-1)
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      const opt = options[activeIndex]
      if (opt) commit(opt)
    }
  }

  const boxStyle: CSSProperties = { ...style }
  const dropdownStyle: CSSProperties = popupMatchSelectWidth
    ? { width: 'var(--radix-popover-trigger-width)' }
    : { minWidth: 'var(--radix-popover-trigger-width)' }

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <PopoverPrimitive.Trigger asChild>
        <div
          ref={triggerRef}
          role="combobox"
          aria-expanded={open}
          aria-disabled={disabled || undefined}
          tabIndex={disabled ? -1 : (tabIndex ?? 0)}
          data-testid={dataTestid}
          onClick={onClick}
          onKeyDown={onKeyDown}
          className={cx(
            'om-select',
            'om-select-single',
            'om-select-show-arrow',
            `om-select-${size}`,
            VARIANT_CLASS[variant],
            disabled && 'om-select-disabled',
            open && 'om-select-open om-select-focused',
            className,
          )}
          style={boxStyle}
        >
          <div className="om-select-selector">
            {selectedOption ? (
              <span className="om-select-selection-item">
                {labelRender
                  ? labelRender({ value: selectedOption.value, label: selectedOption.label })
                  : selectedOption.label}
              </span>
            ) : (
              <span className="om-select-selection-placeholder">{placeholder}</span>
            )}
            <input
              className="om-select-selection-search-input"
              readOnly
              tabIndex={-1}
              aria-hidden="true"
            />
          </div>
          <span className="om-select-arrow" aria-hidden="true">
            {suffixIcon ?? (
              <svg viewBox="0 0 12 12" width="12" height="12" aria-hidden="true">
                <path
                  d="M2.5 4.5L6 8l3.5-3.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.2"
                />
              </svg>
            )}
          </span>
        </div>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal container={getPopupContainer?.(triggerRef.current as HTMLElement)}>
        <PopoverPrimitive.Content
          role="listbox"
          className={cx('om-select-dropdown', popupClassName)}
          align="start"
          sideOffset={4}
          style={dropdownStyle}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          {options.map((opt, index) => {
            const isSelected = opt.value === currentValue
            const isActive = index === activeIndex
            return (
              <div
                key={String(opt.value)}
                role="option"
                aria-selected={isSelected}
                aria-disabled={opt.disabled || undefined}
                className={cx(
                  'om-select-item',
                  'om-select-item-option',
                  isSelected && 'om-select-item-option-selected',
                  isActive && 'om-select-item-option-active',
                  opt.disabled && 'om-select-item-option-disabled',
                )}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => commit(opt)}
              >
                <div className="om-select-item-option-content">
                  {optionRender ? optionRender(opt, { index }) : opt.label}
                </div>
              </div>
            )
          })}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  )
}
